import { weddingPaths } from './paths';
import { MenuItem } from './sitemap';
import type { PlanType } from 'data/plan-config';
import type { EventType } from 'utils/eventType';
import { canUseQuiz, canUseWishes, hasInvitationProduct } from 'utils/plan';

// The wedding-area nav header reads in the event's own voice. Resolved to a
// distinct i18n key (not i18next context) because nav labels render via a
// shared `t(item.key)` across several sidenav components.
const navHeaderKey = (eventType?: EventType): string =>
  eventType === 'baptism'
    ? 'nav.menu.baptism'
    : eventType === 'wedding_baptism'
      ? 'nav.menu.celebration'
      : 'nav.menu.wedding';

/**
 * Wedding-specific navigation for couples.
 *
 * Heirloom (`pro`) couples see the full set; Blossom and Bouquet couples see
 * the guest-gallery surface — Guests and Seating belong to the
 * editorial-invitation product and are Heirloom-only. Wishes and Quiz are not
 * part of that bundle and ship with Bouquet too.
 *
 * Plan defaults to 'free' so unauthenticated/early-bootstrap renders pick
 * the most restrictive sitemap rather than briefly flashing premium items.
 */
export const getCoupleSitemap = (
  eventId?: string,
  plan: PlanType = 'free',
  eventType?: EventType,
): MenuItem[] => {
  const isHeirloom = hasInvitationProduct(plan);

  const items: MenuItem['items'] = [
    {
      name: 'Dashboard',
      key: 'nav.menu.dashboard',
      path: weddingPaths.coupleDashboard,
      pathName: 'couple-dashboard',
      icon: 'solar:checklist-minimalistic-bold-duotone',
      active: true,
    },
    // The digital invitation editor. Heirloom-only, but shown on every plan as
    // a teaser — the page gates itself, like the Planning Hub below.
    {
      name: 'Invitation',
      key: 'nav.menu.invitation',
      path: weddingPaths.coupleInvitation,
      pathName: 'couple-invitation',
      // `?section=` opens one of its sections; the item stays selected.
      selectionPrefix: weddingPaths.coupleInvitation,
      icon: 'solar:letter-opened-bold-duotone',
      active: true,
    },
    {
      name: 'Insights',
      key: 'nav.menu.insights',
      path: weddingPaths.coupleInsights,
      pathName: 'couple-insights',
      icon: 'solar:chart-2-bold-duotone',
      active: true,
    },
    // The QR code guests scan to join the album — every plan has it, so it is
    // never gated. The dashboard keeps its own share card as well.
    {
      name: 'QR Code',
      key: 'nav.menu.qr_code',
      path: weddingPaths.coupleQrCode,
      pathName: 'couple-qr-code',
      // Its table-card designer, a page under it, keeps the item selected.
      selectionPrefix: weddingPaths.coupleQrCode,
      icon: 'solar:qr-code-bold-duotone',
      active: true,
    },
    // Planning Hub (Organizer). Heirloom-only feature, but the nav item shows
    // for every plan as a locked-teaser upsell surface — the page itself gates
    // (ProPlanCTA) for non-Heirloom couples. So it lives OUTSIDE the isHeirloom
    // block, unlike Guests/Seating/Wishes/Quiz which hide their nav entirely.
    {
      name: 'Planning Hub',
      key: 'nav.menu.organizer',
      path: weddingPaths.coupleOrganizer,
      pathName: 'couple-organizer',
      icon: 'solar:clipboard-list-bold-duotone',
      active: true,
    },
    // Guests / RSVP management is part of the editorial-invitation product
    // and only renders for Heirloom couples. Free + Bouquet share photos
    // via QR / link without any guest-list flow on the dashboard.
    ...(isHeirloom
      ? [
          {
            name: 'Guests',
            key: 'nav.menu.guests',
            path: weddingPaths.coupleGuests,
            pathName: 'couple-guests',
            icon: 'solar:users-group-rounded-bold-duotone',
            active: true,
          },
          {
            name: 'Seating',
            key: 'nav.menu.seating',
            path: weddingPaths.coupleSeating,
            pathName: 'couple-seating',
            icon: 'solar:widget-3-bold-duotone',
            active: true,
          },
        ]
      : []),
    {
      name: 'Gallery',
      key: 'nav.menu.gallery',
      path: eventId ? weddingPaths.guestGallery(eventId) : weddingPaths.coupleDashboard,
      pathName: 'couple-gallery',
      icon: 'solar:gallery-bold-duotone',
      active: true,
    },
    {
      name: 'Upload',
      key: 'nav.menu.upload',
      path: weddingPaths.coupleUpload,
      pathName: 'couple-upload',
      icon: 'solar:upload-bold-duotone',
      active: true,
    },
    // Wishes and Quiz are paid-plan hooks (Bouquet + Heirloom) — they run on
    // the QR-joined attendee, so they don't need the invitation product.
    // Backend already 403s them for Blossom (`requireWishesPlan` /
    // `requireQuizPlan`); hiding the sidebar entries avoids a dead-link UX.
    ...(canUseWishes(plan)
      ? [
          {
            name: 'Wishes',
            key: 'nav.menu.wishes',
            path: weddingPaths.coupleWishes,
            pathName: 'couple-wishes',
            icon: 'solar:videocamera-bold-duotone',
            active: true,
          },
        ]
      : []),
    ...(canUseQuiz(plan)
      ? [
          {
            name: 'Quiz',
            key: 'nav.menu.quiz',
            path: weddingPaths.coupleQuiz,
            pathName: 'couple-quiz',
            icon: 'solar:question-square-bold-duotone',
            active: true,
          },
        ]
      : []),
  ];

  return [
    {
      id: 'wedding-main',
      subheader: '',
      key: navHeaderKey(eventType),
      icon: 'material-symbols:favorite-outline',
      items,
    },
  ];
};

/**
 * Couple mobile bottom-nav: ordered destination priority by lifecycle phase.
 *
 * The bottom bar shows the first {@link COUPLE_BOTTOM_NAV_MAX} of these that
 * exist in the couple's sitemap (plan/eventType gating has already pruned the
 * rest); everything after that falls into the "More" sheet, in the same order.
 * The desktop sidenav is unaffected — it always renders the full sitemap.
 *
 * Rationale: before the event the couple is *planning* (Guests, Seating);
 * after it they're *reliving* it (Album, Wishes, Quiz). Insights has little to
 * show pre-event but matters post-event, so it flexes into the slot an
 * unpublished Quiz would otherwise have taken (`hasQuiz === false`). Dashboard
 * and Gallery are anchors in both phases and never move. The QR code is what
 * gets printed before the day, so it leads Insights pre-event and trails it
 * after. The invitation is what a Heirloom couple shapes before the day, so it
 * comes second then; for other plans it is a teaser, kept in "More", and after
 * the day it has been sent.
 */
export const COUPLE_BOTTOM_NAV_MAX = 4;

export const getCoupleBottomNavOrder = (
  isPostEvent: boolean,
  hasQuiz: boolean,
  isHeirloom: boolean,
): string[] =>
  isPostEvent
    ? [
        'couple-dashboard',
        'couple-gallery',
        'couple-wishes',
        ...(hasQuiz ? ['couple-quiz'] : []),
        'couple-insights',
        'couple-qr-code',
        'couple-organizer',
        'couple-invitation',
        'couple-upload',
        'couple-guests',
        'couple-seating',
        ...(hasQuiz ? [] : ['couple-quiz']),
      ]
    : [
        'couple-dashboard',
        ...(isHeirloom ? ['couple-invitation'] : []),
        'couple-organizer',
        'couple-guests',
        'couple-seating',
        'couple-gallery',
        'couple-qr-code',
        'couple-insights',
        'couple-upload',
        ...(isHeirloom ? [] : ['couple-invitation']),
        'couple-wishes',
        'couple-quiz',
      ];

/**
 * Wedding-specific navigation for guests
 * Shows when user role is 'uploader'.
 *
 * Plan defaults to 'free' so a guest whose session pre-dates the
 * `plan`-on-uploader-session change (or hasn't refreshed) gets the most
 * restrictive sitemap rather than briefly flashing premium items
 * that the BE would 403 anyway.
 */
export const getGuestSitemap = (
  eventId?: string,
  plan: PlanType = 'free',
  eventType?: EventType,
): MenuItem[] => {
  const items: MenuItem['items'] = [
    {
      name: 'Dashboard',
      key: 'nav.menu.dashboard',
      path: eventId ? weddingPaths.guestDashboard(eventId) : '#',
      pathName: 'guest-dashboard',
      icon: 'solar:widget-bold-duotone',
      active: true,
    },
    {
      name: 'Gallery',
      key: 'nav.menu.gallery',
      path: eventId ? weddingPaths.guestGallery(eventId) : '#',
      pathName: 'guest-gallery',
      icon: 'solar:gallery-bold-duotone',
      active: true,
    },
    {
      name: 'Upload',
      key: 'nav.menu.upload',
      path: eventId ? weddingPaths.guestUpload(eventId) : '#',
      pathName: 'guest-upload',
      icon: 'solar:upload-bold-duotone',
      active: true,
    },
    // Wishes ships with Bouquet + Heirloom. Backend already 403s `/wishes`
    // for guests of Blossom events via requireWishesPlan; hiding the sidebar
    // entry avoids the dead-link UX. Mirrors the same gating in
    // getCoupleSitemap above.
    ...(canUseWishes(plan)
      ? [
          {
            name: 'Wishes',
            key: 'nav.menu.wishes',
            path: eventId ? weddingPaths.guestWishes(eventId) : '#',
            pathName: 'guest-wishes',
            icon: 'solar:videocamera-bold-duotone',
            active: true,
          },
        ]
      : []),
  ];

  return [
    {
      id: 'guest-main',
      subheader: '',
      key: navHeaderKey(eventType),
      icon: 'material-symbols:favorite-outline',
      items,
    },
  ];
};
